import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import React, { useCallback, useEffect, useState } from 'react';
import UserPicker from 'components/Pickers/UserPicker';
import TextField from '@mui/material/TextField';
import { SxProps, Theme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MenuItem from '@mui/material/MenuItem';
import { Placement, PlacementRateChangeRequest } from 'common/models/Placements';
import { ConvertPlacementToPerm } from 'services/ChangeRequestsService';
import RWTextFieldComponent from 'components/RWTextFieldComponent';
import { RegexIsPositiveNumberWith2Decimals } from 'util/RegExUtils';
import { NameIdObj } from 'common/models/GenericTypes';

interface Props {
  open: boolean;
  placementId: number;
  rate: PlacementRateChangeRequest | null;
  placement: Placement | null;
  netRevenueLabel?: string;
  isPendingChanges?: boolean;
  closeHandler: () => void;
  loadingHandler?: (isLoading: boolean) => void;
  successHandler?: (message: string) => void;
  errorHandler?: (message: string) => void;
}

const leftStyle: SxProps<Theme> = { flex: '1 1 0', pr: '5px' };
const rightStyle: SxProps<Theme> = { flex: '1 1 0', pl: '5px' };
const validNumberValueRegex = /^-?\d+(\.\d{1,2})?$/;

const daysPerWeekValidator = (value: string) => {
  return value === '' || (RegexIsPositiveNumberWith2Decimals(value) && +value <= 7);
};

const hoursPerDaysValidator = (value: string) => {
  return value === '' || (RegexIsPositiveNumberWith2Decimals(value) && +value <= 24);
};

const numberFieldValidator = (value: string) => {
  return value === '' || (RegexIsPositiveNumberWith2Decimals(value) && value.length < 11);
};

export default function ConvertToPermDialog({
  open,
  placementId,
  rate,
  placement,
  netRevenueLabel = 'Net Revenue',
  isPendingChanges,
  closeHandler: closeHandlerProp,
  loadingHandler,
  successHandler,
  errorHandler,
}: Props) {
  const [effectiveDate, setEffectiveDate] = useState<moment.Moment | null>(null);
  const [salary, setSalary] = useState('');
  const [salaryUnits, setSalaryUnits] = useState(0);
  const [invoiceFee, setInvoiceFee] = useState('');
  const [invoiceFeeType, setInvoiceFeeType] = useState(0);
  const [netRevenue, setNetRevenue] = useState('');
  const [hoursPerDay, setHoursPerDay] = useState('8');
  const [daysPerWeek, setDaysPerWeek] = useState(5);
  const [consultant1Id, setConsultant1Id] = useState(0);
  const [consultant1Percent, setConsultant1Percent] = useState('');
  const [consultant2Id, setConsultant2Id] = useState(0);
  const [consultant2Percent, setConsultant2Percent] = useState('');
  const [consultant3Id, setConsultant3Id] = useState(0);
  const [consultant3Percent, setConsultant3Percent] = useState('');
  const [consultant4Id, setConsultant4Id] = useState(0);
  const [consultant4Percent, setConsultant4Percent] = useState('');
  const [consultant5Id, setConsultant5Id] = useState(0);
  const [consultant5Percent, setConsultant5Percent] = useState('');
  const [payComments, setPayComments] = useState('');

  useEffect(() => {
    if (open && rate && placement) {
      setEffectiveDate(null);
      setSalary('');
      setSalaryUnits(0);
      setInvoiceFee('');
      setInvoiceFeeType(0);
      setHoursPerDay(rate.hoursPerDay.toString());
      setDaysPerWeek(rate.daysPerWeek);

      setConsultant1Id(rate.consultantID1);
      setConsultant2Id(rate.consultantID2);
      setConsultant3Id(rate.consultantID3);
      setConsultant4Id(rate.consultantID4);
      setConsultant5Id(rate.consultantID5);

      setConsultant1Percent(rate.consultantPercentage1.toString());
      setConsultant2Percent(rate.consultantPercentage2.toString());
      setConsultant3Percent(rate.consultantPercentage3.toString());
      setConsultant4Percent(rate.consultantPercentage4.toString());
      setConsultant5Percent(rate.consultantPercentage5.toString());

      setPayComments(placement.payComments ?? '');
    }
  }, [open, rate, placement]);

  const closeHandler = useCallback(() => {
    closeHandlerProp();
    errorHandler && errorHandler('');
  }, [closeHandlerProp, errorHandler]);

  useEffect(() => {
    if (open && isPendingChanges)
      errorHandler && errorHandler('There is already a pending Change Request');
  }, [errorHandler, isPendingChanges, open]);

  const submitCallback = useCallback(async () => {
    loadingHandler && loadingHandler(true);
    const rateId = rate ? rate.id : 0;
    const p1 = consultant1Percent.trim() === '' ? '0' : consultant1Percent;
    const p2 = consultant2Percent.trim() === '' ? '0' : consultant2Percent;
    const p3 = consultant3Percent.trim() === '' ? '0' : consultant3Percent;
    const p4 = consultant4Percent.trim() === '' ? '0' : consultant4Percent;
    const p5 = consultant5Percent.trim() === '' ? '0' : consultant5Percent;
    const feeValue = invoiceFee.trim() === '' ? '0' : invoiceFee;
    const salaryPackage = salary.trim() === '' ? '0' : salary;

    const efDate =
      effectiveDate && effectiveDate.isValid() ? effectiveDate.format('YYYY-MM-DD') : '';
    const res = await ConvertPlacementToPerm(
      placementId,
      {
        consultantId1: consultant1Id,
        consultantId2: consultant2Id,
        consultantId3: consultant3Id,
        consultantId4: consultant4Id,
        consultantId5: consultant5Id,
        consultantPercentage1: +p1,
        consultantPercentage2: +p2,
        consultantPercentage3: +p3,
        consultantPercentage4: +p4,
        consultantPercentage5: +p5,
        netRevenue: +netRevenue,
        daysPerWeek: daysPerWeek,
        hoursPerDay: +hoursPerDay,
        effectiveDate: efDate,
        feeType: invoiceFeeType,
        feeValue: +feeValue,
        rateId: rateId,
        salaryPackage: +salaryPackage,
        salaryUnits: salaryUnits,
        payComments: payComments,
      },
      errorHandler
    );
    if (res) {
      successHandler && successHandler('Change Request Submitted');
      closeHandler();
    }

    loadingHandler && loadingHandler(false);
  }, [
    placementId,
    rate,
    effectiveDate,
    netRevenue,
    daysPerWeek,
    hoursPerDay,
    consultant1Id,
    consultant2Id,
    consultant3Id,
    consultant4Id,
    consultant5Id,
    consultant1Percent,
    consultant2Percent,
    consultant3Percent,
    consultant4Percent,
    consultant5Percent,
    invoiceFee,
    invoiceFeeType,
    salary,
    salaryUnits,
    payComments,
    closeHandler,
    loadingHandler,
    successHandler,
    errorHandler,
  ]);

  const onSalaryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if ((val.endsWith('.') && !val.endsWith('..')) || val.match(validNumberValueRegex)) {
      setSalary(val);
    }
  }, []);

  const onInvoiceFeeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if ((val.endsWith('.') && !val.endsWith('..')) || val.match(validNumberValueRegex)) {
      setInvoiceFee(val);
    }
  }, []);

  const onPercent1Change = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if ((val.endsWith('.') && !val.endsWith('..')) || val.match(validNumberValueRegex)) {
      setConsultant1Percent(val);
    }
  }, []);

  const onPercent2Change = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if ((val.endsWith('.') && !val.endsWith('..')) || val.match(validNumberValueRegex)) {
      setConsultant2Percent(val);
    }
  }, []);

  const onPercent3Change = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if ((val.endsWith('.') && !val.endsWith('..')) || val.match(validNumberValueRegex)) {
      setConsultant3Percent(val);
    }
  }, []);

  const onPercent4Change = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if ((val.endsWith('.') && !val.endsWith('..')) || val.match(validNumberValueRegex)) {
      setConsultant4Percent(val);
    }
  }, []);

  const onPercent5Change = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val === '')
      if ((val.endsWith('.') && !val.endsWith('..')) || val.match(validNumberValueRegex)) {
        setConsultant5Percent(val);
      }
  }, []);

  const consultant1ChangeHanlder = useCallback((c: NameIdObj | null) => {
    if (!c) setConsultant1Percent('0');
    setConsultant1Id(c ? c.id : 0);
  }, []);

  const consultant2ChangeHanlder = useCallback((c: NameIdObj | null) => {
    if (!c) setConsultant2Percent('0');
    setConsultant2Id(c ? c.id : 0);
  }, []);

  const consultant3ChangeHanlder = useCallback((c: NameIdObj | null) => {
    if (!c) setConsultant3Percent('0');
    setConsultant3Id(c ? c.id : 0);
  }, []);

  const consultant4ChangeHanlder = useCallback((c: NameIdObj | null) => {
    if (!c) setConsultant4Percent('0');
    setConsultant4Id(c ? c.id : 0);
  }, []);

  const consultant5ChangeHanlder = useCallback((c: NameIdObj | null) => {
    if (!c) setConsultant5Percent('0');
    setConsultant5Id(c ? c.id : 0);
  }, []);

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle>Convert to Perm</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt="10px">
          <DatePicker
            label="Effective Date"
            value={effectiveDate}
            onChange={(m) => setEffectiveDate(m)}
            slotProps={{ actionBar: { actions: ['clear', 'today', 'cancel', 'accept'] } }}
          />
          <Box display="flex">
            <RWTextFieldComponent
              value={salary}
              label="Salary"
              placeholder="From"
              sxOptions={leftStyle}
              onChange={onSalaryChange}
              validator={numberFieldValidator}
            />
            <TextField
              select
              value={salaryUnits.toString()}
              label="Salary Units"
              sx={rightStyle}
              onChange={({ target }) => setSalaryUnits(+target.value)}
            >
              <MenuItem key={0} value="0">
                Select
              </MenuItem>
              <MenuItem key={1} value="1">
                Hourly
              </MenuItem>
              <MenuItem key={2} value="2">
                Daily
              </MenuItem>
              <MenuItem key={3} value="3">
                Weekly
              </MenuItem>
              <MenuItem key={4} value="4">
                Monthly
              </MenuItem>
              <MenuItem key={5} value="5">
                Yearly
              </MenuItem>
            </TextField>
          </Box>
          <Box display="flex">
            <RWTextFieldComponent
              value={invoiceFee}
              label="Invoice Fee"
              sxOptions={leftStyle}
              onChange={onInvoiceFeeChange}
              validator={numberFieldValidator}
            />
            <TextField
              select
              value={invoiceFeeType.toString()}
              label="Invoice Fee Type"
              sx={rightStyle}
              onChange={({ target }) => setInvoiceFeeType(+target.value)}
            >
              <MenuItem value="0">Select</MenuItem>
              <MenuItem value="1">Percentage</MenuItem>
              <MenuItem value="2">Fixed</MenuItem>
            </TextField>
          </Box>
          <RWTextFieldComponent
            value={netRevenue}
            label={netRevenueLabel}
            onChange={({ target }) => setNetRevenue(target.value)}
            validator={numberFieldValidator}
          />
          <RWTextFieldComponent
            value={hoursPerDay}
            label="Hours Per Day"
            onChange={(e) => setHoursPerDay(e.target.value)}
            validator={hoursPerDaysValidator}
          />
          <RWTextFieldComponent
            label="Days Per Week"
            value={daysPerWeek.toString()}
            onChange={({ target }) => setDaysPerWeek(+target.value)}
            validator={daysPerWeekValidator}
          />
          <Box display="flex">
            <UserPicker
              label="Consultant 1"
              userId={consultant1Id}
              onSelect={consultant1ChangeHanlder}
              sx={leftStyle}
            />
            <RWTextFieldComponent
              label="Consultant 1 %"
              value={consultant1Percent}
              onChange={onPercent1Change}
              disabled={consultant1Id === 0}
              sxOptions={rightStyle}
              validator={numberFieldValidator}
            />
          </Box>
          <Box display="flex">
            <UserPicker
              label="Consultant 2"
              userId={consultant2Id}
              onSelect={consultant2ChangeHanlder}
              sx={leftStyle}
            />
            <RWTextFieldComponent
              label="Consultant 2 %"
              value={consultant2Percent}
              onChange={onPercent2Change}
              disabled={consultant2Id === 0}
              sxOptions={rightStyle}
              validator={numberFieldValidator}
            />
          </Box>
          <Box display="flex">
            <UserPicker
              label="Consultant 3"
              userId={consultant3Id}
              onSelect={consultant3ChangeHanlder}
              sx={leftStyle}
            />
            <RWTextFieldComponent
              label="Consultant 3 %"
              value={consultant3Percent}
              onChange={onPercent3Change}
              disabled={consultant3Id === 0}
              sxOptions={rightStyle}
              validator={numberFieldValidator}
            />
          </Box>
          <Box display="flex">
            <UserPicker
              label="Consultant 4"
              userId={consultant4Id}
              onSelect={consultant4ChangeHanlder}
              sx={leftStyle}
            />
            <RWTextFieldComponent
              label="Consultant 4 %"
              value={consultant4Percent}
              onChange={onPercent4Change}
              disabled={consultant4Id === 0}
              sxOptions={rightStyle}
              validator={numberFieldValidator}
            />
          </Box>
          <Box display="flex">
            <UserPicker
              label="Consultant 5"
              userId={consultant5Id}
              onSelect={consultant5ChangeHanlder}
              sx={leftStyle}
            />
            <RWTextFieldComponent
              label="Consultant 5 %"
              value={consultant5Percent}
              onChange={onPercent5Change}
              disabled={consultant5Id === 0}
              sxOptions={rightStyle}
              validator={numberFieldValidator}
            />
          </Box>
          <RWTextFieldComponent
            value={payComments}
            label="Pay Comments"
            onChange={({ target }) => setPayComments(target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="error" onClick={closeHandler}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          disabled={!effectiveDate}
          onClick={submitCallback}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
